import type { ReactNode } from "react";
import {
  Icon,
  MediaPreview,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaOpportunityDisplayRow } from "../../../lib/persona-opportunity-row-model.js";

export interface PersonaOpportunitySupportingMediaProps {
  row: PersonaOpportunityDisplayRow;
  /** Visual size variant — card hero vs compact table cell. */
  variant: "card" | "table";
  className?: string;
  children?: ReactNode;
}

function PlayGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function CompetitorBrandStrip({
  row,
  variant,
}: {
  row: PersonaOpportunityDisplayRow;
  variant: "card" | "table";
}) {
  if (!row.competitorName || !row.competitorInitial) return null;

  const isCard = variant === "card";

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 bottom-0 z-[1] flex items-end gap-1.5",
        isCard ? "bg-linear-to-t from-black/75 via-black/35 to-transparent px-2 pb-2 pt-8" : "p-0.5",
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20 font-semibold text-white ring-1 ring-white/35 backdrop-blur-sm",
          isCard ? "size-7 text-[11px]" : "size-4 text-[8px]",
        )}
        aria-hidden
      >
        {row.competitorProfilePictureUrl ? (
          <img
            src={row.competitorProfilePictureUrl}
            alt=""
            className="size-full object-cover"
          />
        ) : (
          row.competitorInitial
        )}
      </span>
      {isCard ? (
        <p className="min-w-0 truncate pb-0.5 text-[12px] font-semibold leading-tight text-white">
          {row.competitorName}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Supporting-ad thumb with MediaPreview lightbox when `mediaUrl` is present.
 * Without a media URL, shows a disabled play affordance (plan R: graceful no-CDN case).
 */
export function PersonaOpportunitySupportingMedia({
  row,
  variant,
  className,
  children,
}: PersonaOpportunitySupportingMediaProps) {
  const { t } = useTranslation();
  const hasMedia = Boolean(row.mediaUrl);
  const hasThumbnail = Boolean(row.thumbnailUrl);
  const playUnavailable = t(
    "personaOpportunities.active.playUnavailable",
    "Video unavailable for this opportunity.",
  );
  const daysMeta =
    row.daysLive != null
      ? t("personaOpportunities.active.daysLive", {
          count: row.daysLive,
          defaultValue: "{{count}}d live",
        })
      : null;
  const lightboxTitle = row.competitorName ?? row.angle;
  const lightboxMeta = daysMeta ? <span>{daysMeta}</span> : undefined;

  const shellClass =
    variant === "card"
      ? "size-full overflow-hidden rounded-sm bg-muted aspect-[9/16]"
      : "h-16 w-12 overflow-hidden rounded-sm bg-muted";

  if (!hasThumbnail && !hasMedia) {
    return (
      <div className={cn("relative flex items-center justify-center", shellClass, className)}>
        <Icon name="image" size={variant === "card" ? "md" : "xs"} className="text-muted-foreground" />
        <CompetitorBrandStrip row={row} variant={variant} />
        {children}
      </div>
    );
  }

  if (!hasMedia) {
    return (
      <div className={cn("relative", shellClass, className)}>
        {hasThumbnail ? (
          <img src={row.thumbnailUrl!} alt="" className="size-full object-cover" />
        ) : null}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "absolute inset-0 z-[2] flex items-center justify-center",
                  variant === "table" && "bg-black/10",
                )}
                aria-label={playUnavailable}
              >
                <span
                  className={cn(
                    "flex items-center justify-center rounded-full bg-black/35 text-white/50 ring-1 ring-white/20",
                    variant === "card" ? "size-10" : "size-6",
                  )}
                >
                  <PlayGlyph className={variant === "card" ? "size-5" : "size-3"} />
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{playUnavailable}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <CompetitorBrandStrip row={row} variant={variant} />
        {children}
      </div>
    );
  }

  return (
    <MediaPreview
      src={row.mediaUrl!}
      thumbnailSrc={row.thumbnailUrl ?? ""}
      type="video"
      alt={lightboxTitle}
      title={lightboxTitle}
      logoSrc={row.competitorProfilePictureUrl ?? undefined}
      meta={lightboxMeta}
      className={cn("group", shellClass, className)}
    >
      <div
        className={cn(
          "pointer-events-none absolute inset-0 z-[2] flex items-center justify-center",
          variant === "card" && "transition-opacity duration-200 group-hover:opacity-0",
        )}
      >
        <span
          className={cn(
            "flex items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]",
            variant === "card" ? "size-10" : "size-6",
          )}
        >
          <PlayGlyph className={variant === "card" ? "size-5" : "size-3"} />
        </span>
      </div>
      <CompetitorBrandStrip row={row} variant={variant} />
      {children}
    </MediaPreview>
  );
}
